/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//standard libraries
import { Canceler } from "axios";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

//custom libraries
import * as yup from "yup";
import {
  LOGIN_SUCCESS_ACTION,
  LOGOUT_SUCCESS_ACTION,
  RESET_ERROR_ACTION,
  SESSION_ERROR_ACTION,
} from "../constants/actions";
import { CSR_ROLE_ID, CSS_ROLE_ID } from "../constants/common";
import { loginSchema } from "../containers/login-form";
import { useCSRValue, useProgressValue } from "../context";
import { usePersonHandler, useUserResource } from "./rest";
import { useError } from "./use-error";

export const useLoginForm = () => {
  const { csrLogin, csrLoginAndChangePassword } = usePersonHandler();
  const { setInProgress } = useProgressValue();
  const { t } = useTranslation();
  const { transformError } = useError();
  const { getCSRRoles } = useUserResource();
  const { dispatch } = useCSRValue();

  const [userName, setUserName] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");

  const [currentPassword, setCurrentPassword] = React.useState<string>("");
  const [passwordNew, setPasswordNew] = React.useState<string>("");
  const [passwordVerify, setPasswordVerify] = React.useState<string>("");

  const canChangePassword = () => {
    return (
      currentPassword &&
      passwordNew &&
      passwordVerify &&
      passwordNew === passwordVerify
    );
  };

  const cancelChangePassword = () => {
    setPasswordNew("");
    setCurrentPassword("");
    setPasswordVerify("");
    dispatch({
      type: RESET_ERROR_ACTION,
    });
  };

  const changePasswordLogin = (evt: any) => {
    evt.preventDefault();
    setInProgress(true);
    const [res, canceler1] = csrLoginAndChangePassword(
      userName,
      currentPassword,
      passwordNew,
      passwordVerify
    );
    cancels.cancels = canceler1;

    return res
      .then((r) => r.data)
      .then((csr) => {
        const [roleRes, canceler2] = getCSRRoles(csr);
        console.log(roleRes);
        cancels.cancels = canceler2;
        return roleRes.then((r) => Object.assign(csr, { roles: r.data.items }));
      })
      .then((csr) => {
        const csrRoles = [CSR_ROLE_ID, CSS_ROLE_ID];
        const { roles } = csr;
        const isCSR: boolean = roles.some((r: any) => {
          const rs = String(r.roleId);
          return csrRoles.includes(rs);
        });

        if (isCSR) {
          csr = {
            ...csr,
            logonId: userName,
          };
          return csr;
        } else {
          throw new Error(t("SignIn.NoRole"));
        }
      })
      .then((_csr) => {
        dispatch({
          type: LOGIN_SUCCESS_ACTION,
          payload: _csr,
        });
      })
      .catch((e) => {
        const error = transformError(e);
        error.passwordExpired = true;
        dispatch({
          type: SESSION_ERROR_ACTION,
          payload: { sessionError: error },
        });
      })
      .finally(() => {
        setInProgress(false);
      });
  };

  const onPasswordNew = (value: string) => {
    setPasswordNew(value);
  };

  const onCurrentPassword = (value: string) => {
    setCurrentPassword(value);
  };

  const onPasswordVerify = (value: string) => {
    setPasswordVerify(value);
  };

  const submitLoginForm = (values: yup.InferType<typeof loginSchema>) => {
    const { username, password } = values;
    setInProgress(true);
    login(username, password)
      .then((resp) => {
        const { data } = resp;
        dispatch({
          type: LOGIN_SUCCESS_ACTION,
          payload: {
            token: data.token,
          },
        });
      })
      .catch((e) => {
        const error =
          "Inicio de sesión o contraseña inválidos. Recuerde que la contraseña distingue entre mayúsculas y minúsculas. Por favor, inténtelo de nuevo.";
        dispatch({
          type: SESSION_ERROR_ACTION,
          payload: {
            sessionError: {
              message: error,
            },
          },
        });
      })
      .finally(() => {
        setInProgress(false);
      });
  };
  const changeUsername = (evt: any) => {
    setUserName(evt.target.value);
  };
  const changePassword = (evt: any) => {
    setPassword(evt.target.value);
  };
  const cancels: any = { cancels: [] };

  const logout = () => {
    setInProgress(true);
    dispatch({
      type: LOGOUT_SUCCESS_ACTION,
    });
    setInProgress(false);
  };

  const login = (username: string, password: string) => {
    const [res, canceler1] = csrLogin(username, password);
    cancels.cancels = canceler1;

    return res.then((r) => r.data);
  };

  useEffect(() => {
    return () => {
      cancels.cancels.forEach((cancel: Canceler) => {
        return cancel();
      });
    };
  }, []);

  const [values, setValues] = React.useState({
    showPassword: false,
  });
  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };
  return {
    logout,
    submitLoginForm,
    changeUsername,
    changePassword,
    changePasswordLogin,
    canChangePassword,
    onCurrentPassword,
    onPasswordNew,
    onPasswordVerify,
    cancelChangePassword,
    currentPassword,
    passwordNew,
    passwordVerify,
    userName,
    password,
    values,
    handleClickShowPassword,
  };
};
